import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/joy-ibiza-528-5-july-2026";
const IMAGE_URL = "https://www.528ibiza.com/media/cache/ad/15/ad155a56efa311ba4b7368007ec282d0.png";
const TICKET_URL = "https://sales.ticketing.cm.com/JOY528";
const VENUE_URL = "https://www.528ibiza.com/events/?brand=joy-ibiza";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const LINEUP = [
  "Boss Priester",
  "Dennis Quin",
  "Jesse Maas",
  "Michel De Hey",
  "Ozzie Guven",
  "Paisley Jensen",
  "Robbie Doherty",
];

const KEYWORDS = [
  "JOY Ibiza 528 tickets",
  "JOY Ibiza 5 July 2026",
  "528 Ibiza JOY tickets",
  "Boss Priester 528 Ibiza",
  "Dennis Quin Ibiza 2026",
  "Jesse Maas 528 Ibiza",
  "Michel De Hey Ibiza",
  "Ozzie Guven 528 Ibiza",
  "Paisley Jensen Ibiza",
  "Robbie Doherty 528 Ibiza",
  "Ibiza daytime house party",
  "528 Ibiza Garden tickets",
  "HLT Ibiza concierge",
];

const FAQS = [
  {
    question: "When is JOY Ibiza at 528 Ibiza?",
    answer: "JOY Ibiza takes place at 528 Ibiza on Sunday 5 July 2026 from 17:00 to 23:00, with last entry listed at 20:00 on the official ticket page.",
  },
  {
    question: "Who is playing JOY Ibiza at 528?",
    answer: "The listed lineup is Boss Priester, Dennis Quin, Jesse Maas, Michel De Hey, Ozzie Guven, Paisley Jensen and Robbie Doherty in the 528 Ibiza Garden.",
  },
  {
    question: "Where can I buy JOY Ibiza tickets?",
    answer: "Use the official JOY Ibiza ticket page linked in this HLT guide. Ticket types include General Admission, Group Ticket, Backstage / Onstage and Hospitality options, subject to live availability.",
  },
  {
    question: "Where is 528 Ibiza?",
    answer: "528 Ibiza is an open-air venue in the Benimussa Hills near Sant Antoni de Portmany, Ibiza. The official ticket page lists shuttle bus transport back to San Antonio after the event as included in the booking fee.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "JOY Ibiza at 528 Ibiza 5 July 2026: Tickets, Lineup and HLT Guide",
  description:
    "JOY Ibiza lands at 528 Ibiza on Sunday 5 July 2026 with Boss Priester, Dennis Quin, Jesse Maas, Michel De Hey, Ozzie Guven, Paisley Jensen and Robbie Doherty.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-07-05",
  dateModified: "2026-07-05",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "JOY Ibiza at 528 Ibiza",
  startDate: "2026-07-05T17:00:00+02:00",
  endDate: "2026-07-05T23:00:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "528 Ibiza",
    address: "Sant Antoni de Portmany, Ibiza, Spain",
  },
  performer: LINEUP.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "40",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy JOY Ibiza Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function JoyIbiza528Article() {
  return (
    <>
      <Helmet>
        <title>JOY Ibiza 528 Tickets 5 July 2026 | HLT Guide</title>
        <meta
          name="description"
          content="JOY Ibiza at 528 Ibiza on Sunday 5 July 2026 with Boss Priester, Dennis Quin, Jesse Maas, Michel De Hey, Ozzie Guven, Paisley Jensen and Robbie Doherty. Official tickets and HLT concierge."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="JOY Ibiza at 528 Ibiza 5 July 2026 Tickets" />
        <meta property="og:description" content="Official ticket link, lineup and HLT planning guide for JOY Ibiza at 528 Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JOY Ibiza 528 Tickets 5 July 2026" />
        <meta name="twitter:description" content="Boss Priester, Dennis Quin, Jesse Maas, Michel De Hey, Ozzie Guven, Paisley Jensen and Robbie Doherty at 528 Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Today</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              JOY Ibiza at 528 Ibiza: 5 July 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              JOY Ibiza lands in the 528 Ibiza Garden today for a daytime open-air house session from 17:00 to 23:00. Boss Priester, Dennis Quin, Jesse Maas, Michel De Hey, Ozzie Guven, Paisley Jensen and Robbie Doherty bring the Amsterdam-born brand to the Benimussa Hills.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official JOY tickets" />
              <ConciergeCta children="Plan 528 with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>JOY Ibiza</dd></div>
              <div><dt className="text-white">Venue</dt><dd>528 Ibiza, Garden</dd></div>
              <div><dt className="text-white">Date</dt><dd>Sunday 5 July 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>17:00-23:00 · Last entry 20:00</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>General Admission, Group, Backstage / Onstage and Hospitality options listed</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Daytime house, Dutch party energy and open-air Ibiza groove</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_URL} alt="JOY Ibiza at 528 Ibiza 5 July 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why today matters">
              <p>
                JOY is built for this slot: a daytime outdoor party with a bright, uncomplicated house music identity. The brand started in the Netherlands with beach and outdoor events, then grew across major Dutch venues and festivals. 528 Ibiza gives it the natural island stage: open air, golden-hour energy and a crowd that can arrive early rather than waiting for the usual post-midnight club rhythm.
              </p>
              <p>
                The official 528 listing places JOY Ibiza in the Garden from 17:00 to 23:00. The official ticket page lists last entry at 20:00, which matters. This is not a late-night wander-in event. It is one to plan around afternoon arrival, sunset pacing and an easy move back toward San Antonio afterwards.
              </p>
            </ArticleSection>

            <ArticleSection title="Lineup guide">
              <p>
                Boss Priester and Dennis Quin give the bill its strongest Dutch house anchor. Both names fit the JOY Ibiza sound: clean drums, rolling basslines, bright vocal touches and a confident daytime lift. Jesse Maas and Michel De Hey add more Dutch depth, with De Hey carrying the kind of long-running selector credibility that gives the lineup more weight than a simple party poster.
              </p>
              <p>
                Ozzie Guven brings a UK-leaning groove edge, while Paisley Jensen and Robbie Doherty round out the event with accessible, dancefloor-first energy. For anyone who prefers house music in sunlight rather than a dark club room, this is one of the more natural 528 Ibiza listings of the day.
              </p>
            </ArticleSection>

            <ArticleSection title="Ticket link and timing">
              <p>
                We are linking directly to the official JOY Ibiza ticket page. At the time checked, the page listed General Admission 1st Release, Group Ticket, Backstage / Onstage and Hospitality options, with Early Bird marked sold out. Availability can change quickly on the day, so use the official ticket page before travelling to the venue.
              </p>
              <p>
                The ticket page also states that the booking fee includes shuttle bus transport back to San Antonio after the event. Show your ticket to board. That is a useful detail for groups staying around San Antonio Bay or planning a later second stop.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on CM" />
                <a href={VENUE_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary">View 528 listing -&gt;</a>
              </div>
            </ArticleSection>

            <ArticleSection title="How to plan the day">
              <p>
                Treat JOY Ibiza as the centre of the afternoon rather than a pre-party afterthought. Arrive before the last-entry cut-off, keep the group together for transport, and decide early whether you are heading back to San Antonio after 23:00 or rolling into another island plan.
              </p>
              <p>
                For HLT-style planning, the clean route is simple: day bed or lunch first, 528 for JOY, shuttle or transfer after, then dinner, hotel bar or a second event depending on the group. If you want that shaped properly, HLT can help with hotels, villas, transfers, tables and the wider Ibiza plan.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> JOY Ibiza</li>
                <li><strong className="text-white">Venue:</strong> 528 Ibiza, Garden</li>
                <li><strong className="text-white">Date:</strong> Sunday 5 July 2026</li>
                <li><strong className="text-white">Time:</strong> 17:00-23:00</li>
                <li><strong className="text-white">Last entry:</strong> 20:00</li>
                <li><strong className="text-white">Lineup:</strong> {LINEUP.join(", ")}</li>
                <li><strong className="text-white">Official ticket link:</strong> <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">JOY Ibiza tickets</a></li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                JOY Ibiza at 528 is a clean daytime house option: approachable, open-air, easy to understand and strong enough musically to build the afternoon around. If you are on the island today and want a Garden party before the night takes over, this is the one to check first.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy JOY Ibiza tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Make the day feel easy.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, transfer, table or group route around JOY Ibiza at 528? HLT can shape the day around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Official tickets" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 grid gap-3 text-sm leading-6 text-slate-300">
                <li><strong className="text-white">Arrive early:</strong> last entry is listed at 20:00.</li>
                <li><strong className="text-white">Use the official page:</strong> ticket availability can change on the day.</li>
                <li><strong className="text-white">Plan transport:</strong> the ticket page notes shuttle bus transport back to San Antonio after the event.</li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
